import type {
  EdgeDirection,
  ResolvedBoundaryFrames,
  ResolvedTeamFrames,
  ResolvedNodeStyle,
  ResolvedStyles,
} from "../types/style.js";
import type { ViewSlice } from "../view/view-extract.js";
import { layout } from "./layout.js";
import type { GroupLabelIndex } from "./group-labels.js";
import { CATEGORY_STUB_TAG, categoryOf, type CategoryId } from "./category-collapse.js";
import type {
  ContainerRect,
  CrossingMarks,
  DisplayMode,
  HopMark,
  LayoutNode,
  LayoutResult,
  Rect,
} from "./layout-types.js";
import { renderShape } from "./shapes.js";
import { getShapeContentInset, type ShapeInsets } from "../shapes/shape-registry.js";
import { renderEdge, renderArrowMarker } from "./edge-routing.js";
import { resolveLabelPlacements, buildLabelInputs } from "./label-placement.js";
import { HOP_RADIUS, JUNCTION_RADIUS } from "./crossing-marks.js";
import { layoutDegradedTabs } from "./degraded-tabs.js";
import {
  CHIP_HEIGHT,
  GHOST_CHIP_WIDTH,
  laneBox,
  packCornerLane,
  renderChip,
  renderGhostChip,
  type CornerLane,
  type LaneInset,
} from "./corner-lane.js";
import { metaGlyph, type MetaGlyphName } from "./meta-glyphs.js";
import {
  buildLegendFooter,
  DY_CENTER,
  DY_HANGING,
  el,
  escapeXml,
  truncateToWidth,
  wrapToWidth,
} from "./svg-builder.js";
import { getIconDef, type SvgIconDef } from "../shapes/shape-registry.js";
import {
  CHAR_WIDTH,
  NODE_PADDING_X,
  NODE_PADDING_Y,
  META_FONT_RATIO,
  estimateTextWidth,
  ICON_LABEL_CHAR_WIDTH,
  ICON_DESC_CHAR_WIDTH,
  ICON_LABEL_CJK_WIDTH,
  ICON_DESC_CJK_WIDTH,
  ICON_DESC_MAX_WIDTH,
  teamChipText,
  DESC_MAX_LINES,
  META_GLYPH_GAP,
} from "./rendering-constants.js";
import { edgeStyleKey, nodeStyleKey } from "../resolver/style-resolver.js";
import type { NodeDiffMeta } from "../diff/view-diff.js";
import { DEFAULT_EMPTY_STATE_LABELS, type EmptyStateLabels } from "./empty-state-labels.js";
import { DEPLOY_AFFORDANCE_KIND_SET, displayGroupId } from "../types/ast.js";
import type { Diagnostic, LegendBlock, LegendViewScope } from "../types/ast.js";
import type { LegendUsage } from "../legend/usage.js";
import type { StyleSheet } from "../types/style.js";
import { type DiagramPalette, type DiagramTheme, resolvePalette } from "./palette.js";
import { FACET_DIM_OPACITY, type FacetOverlay } from "./facet-overlay.js";

const GHOST_OPACITY = 0.3;

// Ratio of the description font size (and its estimated char width) to the
// node's base font size in the *rendered* SVG. Unified with layout.ts's
// DESCRIPTION_FONT_RATIO (0.85) in #2366 proposal C so the wrap the renderer
// performs matches the line count layout reserved height for (closes the
// historical 0.8-vs-0.85 divergence noted in Issue #2014, point 3).
const RENDERED_DESC_FONT_RATIO = 0.85;
// Opacity for secondary text (description) and meta chips layered on the
// card color (#2366 proposal E). 0.9 keeps white-on-service at >= 4.5:1
// where the old 0.7 fell to 3.7:1; the meta row previously used
// palette.textSubtle, which fell to 2.3:1 on the service card.
const SECONDARY_TEXT_OPACITY = 0.9;
const META_TEXT_OPACITY = 0.85;

// Icon-mode text layout constants (from design doc)
const ICON_LABEL_MAX_WIDTH = 122; // px available for title text beside an icon body
const ICON_DESC_MAX_LINES = 3;
const ICON_DESC_LINE_HEIGHT = 14; // px
/**
 * Side inset for text on a card with no icon body to work around, so the
 * budget is the card width minus this on both sides. 8 is what
 * `ICON_DESC_MAX_WIDTH` (144) already implies for the 160px card (#2533).
 */
const ICON_CARD_TEXT_INSET = 8;
/** Type sizes the ICON_*_CHAR_WIDTH estimates are calibrated for. */
const ICON_LABEL_FONT_SIZE = 13;
const ICON_DESC_FONT_SIZE = 11;

/**
 * Sanitizes a node ID for use in a CSS fragment identifier (e.g. href="#krs-view-X").
 * Replaces characters that are not alphanumeric, hyphen, or underscore with underscores.
 */
export function sanitizeId(id: string): string {
  return id.replace(/[^a-zA-Z0-9_-]/g, "_");
}

/**
 * Canonical deep-link anchor id for a drillable structural element / view level.
 *
 * Both surfaces that address such an element by URL fragment route through this
 * one function so the grammar can't drift (TPL-219 / TPL-1827):
 *   - the static drill-down SVG (`<g id="…">` + `:target` CSS, this package)
 *   - the SPA history hash for the drillable system/org views (`buildHash` in
 *     `packages/app`, `#krs-…`) — parity-tested
 *
 * Shape: `krs-<viewPrefix>-<sanitizeId(nodeId)>`. `nodeId` is the author-given
 * `id` (never a label — TPL-2167) or the literal `"root"` for a view's
 * top level. `sanitizeId` is idempotent, so passing an already-sanitized
 * segment is safe.
 *
 * Not every fragment is an element anchor: the SPA's single-level whole-view
 * tabs (deploy/matrix) use a bare `#krs-<view>` token and org Tree View is a
 * mode (`#krs-org-tree`); those are intentionally outside this grammar. Full
 * contract: `docs/spec/permalink.md`.
 */
export function anchorId(viewPrefix: string, nodeId: string): string {
  return `krs-${viewPrefix}-${sanitizeId(nodeId)}`;
}

export interface RenderOptions {
  /** Diff state per node id (and per edge key `from->to`) for diff-mode rendering. */
  nodeDiffState?: Map<string, string>;
  edgeDiffState?: Map<string, string>;
  /**
   * Full node diff metadata. When provided, the renderer derives the node's
   * `data-diff-state` from `meta.state` (preferred over `nodeDiffState`) and
   * decorates annotation badges with their own per-badge diff state
   * (Issue #738 / design doc D-2).
   */
  nodeDiffMeta?: Map<string, NodeDiffMeta>;
  /**
   * Diff state per container keyed by container id (deploy: `serviceId`). When
   * present, the matching `<g data-container-id>` emits `data-diff-state` so
   * CSS can highlight whole-container additions/removals (Issue #750).
   */
  containerDiffState?: Map<string, string>;
  /**
   * Localized labels for the empty-state placeholder rendered when the
   * layout has no nodes or containers. When omitted, English fallbacks
   * from `DEFAULT_EMPTY_STATE_LABELS` are used.
   */
  emptyLabels?: EmptyStateLabels;
  /**
   * Legend blocks declared in the source `.krs`. The renderer paints a
   * footer band below the diagram for every block that targets the
   * current view scope (or omits scope). Pair with `styleSheets` so
   * `ref` entries can resolve to swatch colors via the style cascade.
   */
  legends?: LegendBlock[];
  /** Resolved style sheets, used by the legend footer to color `ref` entries. */
  styleSheets?: StyleSheet[];
  /**
   * Tag/annotation/id/kind usage from the file. Lets the legend footer
   * fall back to a neutral swatch for refs that are in use on real nodes
   * but not painted by any style rule (e.g. `[human]`).
   */
  legendUsage?: LegendUsage;
  /**
   * Which view this render produces. Drives the legend's scope filter so
   * a `legend deploy "..."` block does not leak into the system view.
   */
  viewScope?: LegendViewScope;
  /**
   * Diagram theme. Drives the chrome palette (canvas background, legend
   * band, empty-state text, diff indicators, …). Defaults to `"dark"` so
   * existing output is unchanged. The matching built-in `.krs.style`
   * variant is selected by the caller (see `index.ts`).
   */
  theme?: DiagramTheme;
  /**
   * System-view node categories the viewer has collapsed (Issue #1821).
   * Each collapsed category's nodes are replaced by a single ⊕ stub before
   * layout, so the diagram reflows and edges to the hidden nodes drop. Omit
   * (or empty) for the default fully-expanded render. Only meaningful on the
   * system view; see `docs/design/layer-toggles.md`.
   */
  collapsedCategories?: ReadonlySet<CategoryId>;
  /**
   * When true, draw the interactive category controls (the ⊖ collapse buttons
   * and hover extent frames) for the live preview (Issue #1821). Defaults to
   * false so static outputs — SVG export, `/render`, CLI, guide diagrams — stay
   * clean. The ⊕ stub of an already-collapsed category is always drawn (it is
   * content, not chrome).
   */
  interactive?: boolean;
  /**
   * When true, draw the per-node info / deploy buttons in the card's corner
   * lane (Issue #2420). Defaults to false, so static outputs carry no button
   * for a viewer that cannot click it.
   *
   * Deliberately not folded into `interactive`: the two name different viewer
   * capabilities. The VS Code webview handles `data-info-button` but has no
   * category-collapse handling, so one shared flag would either take away its
   * ⓘ or give it a dead ⊖.
   */
  nodeControls?: boolean;
  /**
   * System-view grouping axis. `"team"` (#1858, P2a) bands each node's owning
   * team; `"boundary"` (#1822, P2b) bands by declared `boundary`. Omit for the
   * default un-grouped kind-tier layout.
   */
  groupBy?: "team" | "boundary";
  /**
   * Declared-boundary axis (node id → every boundary it is declared in, #2178).
   * Sourced from `krsFile.boundaryMembership`; consumed by layout only when
   * `groupBy === "boundary"`, which bands each node by its primary membership.
   */
  boundaryMembership?: Map<string, string[]>;
  /**
   * `krsFile.scopedBoundaryMembership` — membership from `boundary` blocks
   * declared inside a node block (#2036). Layout picks the entry for the canvas
   * it is drawing, so these frame their own level only.
   */
  scopedBoundaryMembership?: Map<string, Map<string, string[]>>;
  /**
   * Group ids the model declares on the active axis, in declaration order
   * (`declaredGroupOrder`). Keeps a declared-but-unplaceable group in the group
   * order instead of letting the axis map decide which groups exist (#2178).
   */
  declaredGroupOrder?: readonly string[];
  /**
   * Declared group labels for the active axis (#2133), from
   * `buildGroupLabelIndex(krsFile, groupBy)`; layout resolves them per canvas.
   * Titles the group frames; omitted → frames fall back to the group id.
   */
  groupLabels?: GroupLabelIndex;
  /**
   * Team id → declared `label`, from `buildTeamLabelIndex(krsFile)`. Supplies
   * the owner chip's display string on every axis; omitted → the chip falls
   * back to the team id (Issue #2157).
   */
  teamLabels?: ReadonlyMap<string, string>;
  collapsedGroups?: ReadonlySet<string>;
  /**
   * Whether the in-place expansion ⊕/⊖ controls may be drawn (Issue #1921).
   * `render()` sets it from the slice's system count so the affordance only
   * appears on the single-system root, where expansion is actually derived.
   * Internal — set by `render()`, not a public compile option.
   */
  expandable?: boolean;
  /**
   * Where `render` reports the diagnostics only a laid-out view can state
   * (#2179) — today, `boundary-membership-not-drawn`. Whether a boundary's frame
   * can reach a card depends on where the cards landed, so the parser cannot
   * know it and the compile pipeline cannot derive it without laying out.
   *
   * A sink rather than a return value because `render` returns the SVG string,
   * and it is called from surfaces that render *many* views into one artifact
   * (the drill-down and all-layers bundles call it once per level); collecting
   * into a caller-owned array is what lets those accumulate. This mirrors how
   * `ResolvedStyles.warnings` is collected.
   *
   * **Every surface that passes `groupBy` should pass this too** — the `◇` tab
   * is drawn from the layout on all of them, so a surface that omits the sink
   * draws the fallback while its diagnostics list stays silent, which is the
   * split TPL-1983 rules out. Entries are deduplicated on push, so a membership
   * that degrades at several levels of one bundle is stated once.
   */
  diagnosticSink?: Diagnostic[];
  /**
   * Resolved facet overlay (#2174). Absent means the overlay is off, and the
   * renderer then emits nothing extra — that absence is what keeps
   * "no facet selected ⇒ byte-identical output" true.
   */
  facetOverlay?: FacetOverlay;
  /**
   * Localized heading for the overlay's legend block. Core keeps an English
   * default, the way `emptyLabels` / `annotationBadgeLabels` do — the renderer
   * never reaches for a translation table.
   */
  facetLegendTitle?: string;
}

/**
 * Derives the legend render scope for a logical-view slice (Issue #1513).
 *
 * - The root view (`slice.systems` populated — the system list) is scope
 *   `system`.
 * - Drill-down levels take the scope named after their root node's kind:
 *   `service` / `domain`.
 * - Other drill roots (a system frame, a usecase, …) have no scope keyword
 *   in the legend vocabulary — `undefined` suppresses the footer there.
 */
export function legendScopeForLogicalSlice(slice: ViewSlice): LegendViewScope | undefined {
  if (slice.systems.length > 0) return "system";
  const kind = slice.containerNode?.kind;
  if (kind === "service") return "service";
  if (kind === "domain") return "domain";
  return undefined;
}

export function render(
  viewSlice: ViewSlice,
  styles: ResolvedStyles,
  serviceIdsWithDeploy?: Set<string>,
  ownerIndex?: Map<string, string>,
  displayMode?: DisplayMode,
  childLevelLinks?: Map<string, string>,
  options?: RenderOptions,
): string {
  const edgeDirections = new Map<string, EdgeDirection>();
  for (const [key, edgeStyle] of styles.edges) {
    if (edgeStyle.direction !== "auto") edgeDirections.set(key, edgeStyle.direction);
  }
  // Resolved-shape lookup for measureNode's inset-aware sizing (#2366 F) —
  // the same resolveNodeStyle chain renderFromLayout draws with.
  const shapeForNode = (id: string, annotations: readonly string[]): string | undefined =>
    shapeNameOf(resolveNodeStyle(styles, id, annotations).shape);
  // The corner lane the ports must keep clear of (#2422). Resolved here
  // because the lane's width comes from the badge style; layout only knows
  // where the card ended up.
  const palette = resolvePalette(options?.theme);
  const chipZoneFor = (node: LayoutNode): Rect | undefined =>
    // The layout is about to apply the shape insets it was given the hook for,
    // so the lane is measured the way `renderNode` will draw it.
    chipZoneOf(node, node.id, styles, palette, displayMode !== "icon", serviceIdsWithDeploy);
  const layoutResult = layout(viewSlice, {
    ownerIndex,
    shapeForNode,
    chipZoneFor,
    teamLabels: options?.teamLabels,
    boundaryMembership: options?.boundaryMembership,
    scopedBoundaryMembership: options?.scopedBoundaryMembership,
    declaredGroupOrder: options?.declaredGroupOrder,
    groupLabels: options?.groupLabels,
    displayMode,
    layoutHints: styles.layoutHints,
    edgeDirections,
    collapsedCategories: options?.collapsedCategories,
    groupBy: options?.groupBy,
    collapsedGroups: options?.collapsedGroups,
    edgeDiffState: options?.edgeDiffState,
    // Diff state reaches the layout so a removed node cannot be claimed by a
    // band-less boundary (#2176); it must stay in the frame ADR-1886 returns it to.
    nodeDiffState: options?.nodeDiffState,
    // Layout needs the membership only to re-derive it onto collapse stubs; it
    // never influences placement, which is what keeps the overlay orthogonal to
    // the Group-by axis (#2174).
    facetMembership: options?.facetOverlay?.membership,
    facetOrder: options?.facetOverlay?.entries.map((e) => e.id),
  });
  const sink = options?.diagnosticSink;
  if (sink) {
    for (const { nodeId, boundaryId } of layoutResult.degradedMemberships ?? []) {
      // The drill-down and all-layers bundles render every level into one
      // artifact through this same call, and a membership can degrade on more
      // than one of them. The fact is the same fact, so state it once.
      const already = sink.some(
        (d) =>
          d.code === "boundary-membership-not-drawn" &&
          d.params.nodeId === nodeId &&
          d.params.boundaryId === boundaryId,
      );
      if (already) continue;
      sink.push({
        severity: "info",
        code: "boundary-membership-not-drawn",
        params: { nodeId, boundaryId },
      });
    }
  }
  const title =
    layoutResult.containers.length === 0 && viewSlice.containerNode
      ? (viewSlice.containerNode.label ?? viewSlice.containerNode.id)
      : undefined;
  return renderFromLayout(
    layoutResult,
    styles,
    title,
    serviceIdsWithDeploy,
    displayMode,
    childLevelLinks,
    // Expansion controls only make sense on the single-system root, where view
    // extraction actually derives expansion (#1921). Drill-down levels have an
    // empty `systems` list and no service nodes, so they never draw one either.
    { ...options, expandable: viewSlice.systems.length <= 1 },
  );
}

export function renderFromLayout(
  layoutResult: LayoutResult,
  styles: ResolvedStyles,
  title?: string,
  serviceIdsWithDeploy?: Set<string>,
  displayMode?: DisplayMode,
  childLevelLinks?: Map<string, string>,
  options?: RenderOptions,
): string {
  const palette = resolvePalette(options?.theme);
  if (layoutResult.nodes.size === 0 && layoutResult.containers.length === 0) {
    return el(
      "svg",
      {
        xmlns: "http://www.w3.org/2000/svg",
        width: "100%",
        height: "100%",
        viewBox: "0 0 200 100",
      },
      el(
        "text",
        {
          x: 100,
          y: 50,
          "text-anchor": "middle",
          fill: palette.emptyStateText,
          "font-family": "sans-serif",
        },
        escapeXml(options?.emptyLabels?.systemNoNodes ?? DEFAULT_EMPTY_STATE_LABELS.systemNoNodes),
      ),
    );
  }

  assignChipZones(layoutResult, styles, palette, serviceIdsWithDeploy);

  const padding = 40;
  const width = layoutResult.width + padding;
  const height = layoutResult.height + padding;

  const parts: string[] = [];

  // Defs: arrow markers
  const defParts: string[] = [];
  const defaultEdgeColor = styles.defaultEdgeStyle.color;
  defParts.push(renderArrowMarker("arrow-default", defaultEdgeColor));

  const edgeColors = new Set<string>();
  for (const [, style] of styles.edges) {
    edgeColors.add(style.color);
  }
  let markerIdx = 0;
  const colorToMarkerId = new Map<string, string>();
  colorToMarkerId.set(defaultEdgeColor, "arrow-default");
  for (const color of edgeColors) {
    if (!colorToMarkerId.has(color)) {
      const id = `arrow-${markerIdx++}`;
      defParts.push(renderArrowMarker(id, color));
      colorToMarkerId.set(color, id);
    }
  }
  parts.push(el("defs", {}, ...defParts));

  // Background
  parts.push(el("rect", { width, height, fill: palette.canvasBg, rx: 0 }));

  // Title label (when no containers — e.g., system-level view)
  if (title) {
    parts.push(
      el(
        "text",
        {
          x: padding / 2,
          y: padding / 2 + 4,
          fill: palette.textMuted,
          "font-size": "14px",
          "font-family": "sans-serif",
          "font-weight": "bold",
        },
        escapeXml(title),
      ),
    );
  }

  // Ghost ancestor containers (outermost first)
  for (const container of layoutResult.containers) {
    if (container.ghost) {
      const containerStyle = styles.nodes.get(container.id) ?? styles.defaultNodeStyle;
      parts.push(renderContainer(container, containerStyle, true));
    }
  }

  // Focused container
  for (const container of layoutResult.containers) {
    if (!container.ghost) {
      const containerStyle = styles.nodes.get(container.id) ?? styles.defaultNodeStyle;
      const diffState = options?.containerDiffState?.get(container.id);
      parts.push(
        renderContainer(
          container,
          containerStyle,
          false,
          diffState,
          palette,
          styles.boundaryFrames,
          styles.teamFrames,
        ),
      );
    }
  }

  // Ghost edges — ghost wrapper dims children to GHOST_OPACITY so they read
  // as peripheral. When diff mode is active and a ghost edge has a non-
  // "unchanged" state, move it to the normal-edges group so the diff colors
  // are not washed out by the wrapper opacity.
  const ghostEdgeParts: string[] = [];
  const normalEdgeParts: string[] = [];
  // A collapsed team's re-targeted stub edges are keyed by the stub id, which
  // the original `edgeDiffState` (keyed on pre-collapse endpoints) cannot match;
  // `layout` folds their diff state onto the stub key, so overlay it here so the
  // stub edge keeps its `data-diff-state` (#1886). Stub keys use the reserved
  // `__group_collapsed_*__` prefix, so they never collide with real edge keys.
  // Same shape as the diff-state merge below: a collapse stub is keyed by the
  // stub id, which the pre-collapse membership cannot match, so the folded map
  // overlays it (#2174 / TPL-1886).
  const overlay = options?.facetOverlay;
  const facetMembership: ReadonlyMap<string, readonly string[]> | undefined = overlay
    ? layoutResult.foldedFacetMembership
      ? new Map<string, readonly string[]>([
          ...overlay.membership,
          ...layoutResult.foldedFacetMembership,
        ])
      : overlay.membership
    : undefined;
  const facetsFor = (nodeId: string, layoutId: string): readonly string[] =>
    facetMembership?.get(nodeId) ?? facetMembership?.get(layoutId) ?? [];
  /**
   * An edge's own selected facets (#2544), read off the layout edge rather than
   * out of a membership map: an edge's `canonicalId` is `undefined` whenever its
   * base form collided (ADR-1096), so there is no id to key it by. Filtered
   * through the overlay's own order — never the edge's declaration order — so
   * the innermost casing is the same facet on every edge, the rule the node
   * rings follow.
   */
  const facetOrder = overlay?.entries.map((e) => e.id) ?? [];
  const edgeFacetsFor = (edgeLayout: LayoutResult["edges"][number]): readonly string[] => {
    const own = edgeLayout.facets;
    if (!overlay || own === undefined) return [];
    return facetOrder.filter((id) => own.includes(id));
  };

  const effectiveEdgeDiffState = layoutResult.foldedEdgeDiffState
    ? new Map<string, string>([
        ...(options?.edgeDiffState ?? new Map<string, string>()),
        ...layoutResult.foldedEdgeDiffState,
      ])
    : options?.edgeDiffState;
  // Resolved stroke of each edge, indexed to match `layoutResult.edges` — so a
  // crossing mark can be drawn in its own edge's colour/width (#1859 P2c-C),
  // not a fixed default that detaches from a coloured diagram.
  // Hops grouped by their host edge index (== position in `layoutResult.edges`,
  // the same array `computeCrossingMarks` indexed), so the host edge's stroke can
  // be gapped where each hop jumps over a crossing (#1859 P2c-C).
  const hopsByEdge = new Map<number, HopMark[]>();
  for (const hop of layoutResult.crossingMarks?.hops ?? []) {
    const list = hopsByEdge.get(hop.edge);
    if (list) list.push(hop);
    else hopsByEdge.set(hop.edge, [hop]);
  }
  // Resolve the edge style once per edge (reused by both the label-placement
  // pre-pass and the render loop below).
  const edgeStyleFor = (edgeLayout: LayoutResult["edges"][number]) => {
    const edgeKey = `${edgeLayout.from}->${edgeLayout.to}`;
    // Prefer the kind-qualified style entry so parallel sync/async edges between
    // the same pair keep their own stroke style; fall back to the bare key for
    // synthetic layout edges (delivers, owns, ghosts, aggregated domain edges).
    return (
      styles.edges.get(edgeStyleKey(edgeLayout.from, edgeLayout.to, edgeLayout.kind)) ??
      styles.edges.get(edgeKey) ??
      styles.defaultEdgeStyle
    );
  };

  // Auto label collision-avoidance (#2048): nudge labels that collide with node
  // cards, with each other, or with another edge's polyline (#2360) off their
  // default midpoint. A label's own edge line is exempt — that is where it
  // belongs. Author-positioned labels (non-default label-position/label-offset)
  // are excluded from moving but still act as obstacles, so author intent wins
  // (ADR-1184 precedence).
  const {
    inputs: labelInputs,
    nodeRects,
    edgeLines,
  } = buildLabelInputs(layoutResult.edges, layoutResult.nodes, edgeStyleFor);
  const labelPlacements = resolveLabelPlacements(labelInputs, nodeRects, edgeLines);

  const edgeStroke: { color: string; strokeWidth: number }[] = [];
  let edgeIndex = 0;
  for (const edgeLayout of layoutResult.edges) {
    const edgeKey = `${edgeLayout.from}->${edgeLayout.to}`;
    const edgeStyle = edgeStyleFor(edgeLayout);
    edgeStroke.push({ color: edgeStyle.color, strokeWidth: edgeStyle.strokeWidth });
    const markerId = colorToMarkerId.get(edgeStyle.color) ?? "arrow-default";
    const diffState = effectiveEdgeDiffState?.get(edgeKey);
    // An edge belongs to the selection two ways, and either one is enough to
    // keep it at full strength: it carries the facet itself (#2544), or it
    // touches a member node. The second is why the rule is "dim when *both*
    // endpoints are outside" — where the highlighted set meets the rest of the
    // model is the main thing an overlay is read for.
    const edgeFacets = edgeFacetsFor(edgeLayout);
    const edgeDimmed =
      overlay !== undefined &&
      edgeFacets.length === 0 &&
      facetsFor(edgeLayout.from, edgeLayout.from).length === 0 &&
      facetsFor(edgeLayout.to, edgeLayout.to).length === 0;
    const rendered = renderEdge(
      edgeLayout,
      edgeStyle,
      markerId,
      diffState,
      hopsByEdge.get(edgeIndex),
      labelPlacements.get(edgeIndex),
      edgeFacets,
      overlay?.colorOf,
    );
    edgeIndex++;
    const withDim = edgeDimmed ? el("g", { opacity: FACET_DIM_OPACITY }, rendered) : rendered;
    const isDimmedGhost =
      edgeLayout.ghost && (diffState === undefined || diffState === "unchanged");
    if (isDimmedGhost) {
      ghostEdgeParts.push(withDim);
    } else {
      normalEdgeParts.push(withDim);
    }
  }
  if (ghostEdgeParts.length > 0) {
    parts.push(el("g", { class: "ghost-edges", opacity: GHOST_OPACITY }, ...ghostEdgeParts));
  }
  parts.push(el("g", { class: "edges" }, ...normalEdgeParts));

  // Crossing marks on top of the edges (#1859 P2c-C): hop arcs neutralise
  // right-angle crossings and junction dots mark trunk merges. Present only in
  // the Group-by view (ungrouped leaves `crossingMarks` undefined — AC-5).
  if (layoutResult.crossingMarks) {
    const { hops, junctions } = layoutResult.crossingMarks;
    if (hops.length > 0 || junctions.length > 0) {
      parts.push(
        renderCrossingMarks(layoutResult.crossingMarks, edgeStroke, styles.defaultEdgeStyle),
      );
    }
  }

  // Nodes (ghost users first, then normal children). As with edges, a ghost
  // node that is diff-tagged (added / removed / changed) gets promoted to the
  // normal group so the diff colors are not flattened by the wrapper opacity.
  const ghostNodeParts: string[] = [];
  const normalNodeParts: string[] = [];
  // The deploy layout encodes per-container instances as `containerId::unitId`
  // (the map `nodeId`), but both resolved styles and diff metadata are keyed by
  // the bare unit id — `layoutNode.id`, the original AST id (Issue #735 / #1666).
  // So the lookups below fall back to `layoutNode.id`, which lets deploy units
  // pick up their resolved style — notably the Icon Mode `shape: url(...)`,
  // without which they hit `defaultNodeStyle` and never render an icon. For
  // system-view nodes `layoutNode.id === nodeId`, so the fallback is a no-op.
  for (const [nodeId, layoutNode] of layoutResult.nodes) {
    const nodeStyle = resolveNodeStyle(styles, nodeId, layoutNode.annotations, layoutNode.id);
    const diffMeta =
      options?.nodeDiffMeta?.get(layoutNode.id) ?? options?.nodeDiffMeta?.get(nodeId);
    const diffState =
      diffMeta?.state ??
      options?.nodeDiffState?.get(layoutNode.id) ??
      options?.nodeDiffState?.get(nodeId);
    const rendered = layoutNode.tags?.includes(CATEGORY_STUB_TAG)
      ? renderCategoryStub(
          layoutNode,
          palette,
          facetsFor(nodeId, layoutNode.id),
          overlay !== undefined,
          overlay?.colorOf,
        )
      : renderNode(
          layoutNode,
          nodeStyle,
          nodeId,
          palette,
          serviceIdsWithDeploy,
          displayMode,
          childLevelLinks,
          diffState,
          diffMeta,
          facetsFor(nodeId, layoutNode.id),
          overlay !== undefined,
          overlay?.colorOf,
          styles.boundaryFrames,
          layoutResult.shapeInsetsApplied ?? false,
          options?.nodeControls ?? false,
        );
    const isDimmedGhost =
      layoutNode.ghost && (diffState === undefined || diffState === "unchanged");
    if (isDimmedGhost) {
      ghostNodeParts.push(rendered);
    } else {
      normalNodeParts.push(rendered);
    }
  }
  if (ghostNodeParts.length > 0) {
    parts.push(el("g", { class: "ghost-nodes", opacity: GHOST_OPACITY }, ...ghostNodeParts));
  }
  parts.push(el("g", { class: "nodes" }, ...normalNodeParts));

  // Collapsible-category controls: a ⊖ button + hover-revealed extent frame for
  // each open `external` / `infra` group on the system view (Issue #1821).
  // Interactive chrome only — omitted from static outputs (export / render / CLI).
  if (options?.interactive) {
    const categoryControls = renderCategoryControls(layoutResult, palette);
    if (categoryControls) parts.push(categoryControls);
    const groupControls = renderGroupControls(layoutResult, palette, options?.collapsedGroups);
    if (groupControls) parts.push(groupControls);
    // In-place expansion is orthogonal to Group by: team and Phase 1 targets the
    // ungrouped, single-system view (view extraction only derives expansion for
    // the single-system root — #1921), so the ⊕/⊖ controls only appear there.
    // `render()` sets `expandable` from the slice's system count; a multi-system
    // root gets no expand affordance (the ⊕ would be a no-op there).
    if (!options?.groupBy && options?.expandable) {
      const expandControls = renderExpandControls(layoutResult, palette);
      if (expandControls) parts.push(expandControls);
    }
  }

  // Legend footer (Issue #887) — rendered as a band below the diagram so
  // it survives panning and is preserved by single-file SVG exports.
  // The footer's height extends the outer viewBox; positioning is handled
  // via a translate at y=height of the diagram body.
  let totalHeight = height;
  // The overlay contributes its own colour key, so the band is drawn whenever
  // *either* an authored legend applies or facets are selected — a reader
  // looking at coloured rings must be able to find out what they mean, even in a
  // model that declares no `legend` at all (#2174).
  const facetLegendBlock =
    overlay && overlay.entries.length > 0
      ? {
          title: options?.facetLegendTitle ?? "Facets",
          rows: overlay.entries.map((e) => ({ color: e.color, label: e.label })),
        }
      : undefined;
  const hasAuthoredLegend =
    !!options?.legends && options.legends.length > 0 && !!options?.viewScope;
  if (hasAuthoredLegend || facetLegendBlock) {
    const footer = buildLegendFooter(
      options?.legends ?? [],
      options?.viewScope ?? "system",
      options?.styleSheets ?? [],
      width,
      palette,
      options?.legendUsage,
      facetLegendBlock,
    );
    if (footer) {
      parts.push(el("g", { transform: `translate(0,${height})` }, footer.svg));
      totalHeight = height + footer.height;
    }
  }

  return el(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: `0 0 ${width} ${totalHeight}`,
      width,
      height: totalHeight,
    },
    ...parts,
  );
}

/**
 * The circle + ⊖/⊕ strokes shared by every collapse affordance — category
 * controls / stubs (#1821) and group controls (#1858). `plus` adds the vertical
 * stroke (⊕ = expand); omit it for ⊖ = collapse. Returns the child elements so
 * the caller wraps them in its own `<g>` (with the right data-attributes).
 */
function collapseGlyph(
  cx: number,
  cy: number,
  plus: boolean,
  palette: DiagramPalette,
  opts?: { radius?: number; fill?: string },
): string[] {
  const parts = [
    el("circle", {
      cx,
      cy,
      r: opts?.radius ?? 9,
      fill: opts?.fill ?? palette.surfaceBg,
      stroke: palette.accent,
      "stroke-width": 1.5,
    }),
    el("line", {
      x1: cx - 4,
      y1: cy,
      x2: cx + 4,
      y2: cy,
      stroke: palette.accent,
      "stroke-width": 1.5,
    }),
  ];
  if (plus) {
    parts.push(
      el("line", {
        x1: cx,
        y1: cy - 4,
        x2: cx,
        y2: cy + 4,
        stroke: palette.accent,
        "stroke-width": 1.5,
      }),
    );
  }
  return parts;
}

/**
 * Render the Group-by crossing marks layer (#1859 P2c-C). Emitted above the edge
 * layer so marks sit on top of the lines.
 *
 * - **hop**: a `<path>` arc that bumps *over* the crossing (crossing = NOT
 *   connected), centred at `(x, y)` and oriented along the host segment via
 *   `angle` (degrees). Elliptical (`rx = halfWidth`, `ry = HOP_RADIUS`) so a
 *   clustered wide hop stays a shallow bump; `sweep = 1` bumps to one side. An
 *   axis-aligned hop (`angle = 0`) renders exactly as the pre-#1939 flat bump.
 * - **junction**: a `<circle>` dot at each trunk merge (merge = connected).
 *
 * Each mark is drawn in its owning edge's resolved colour (and the hop in that
 * edge's stroke width) via `edgeStroke[mark.edge]`, so marks stay visually part
 * of the lines they annotate on a colour-styled diagram; `fallback` covers an
 * out-of-range index. Coordinates are rounded to 2 decimals so tiny float noise
 * never destabilises the SVG snapshot.
 *
 * Scope: crossings in any *single-system* view (right-angle and, since #1939,
 * diagonal; grouped and, since #1956, ungrouped). The multi-system view is
 * extended separately (#1939 Part 2) — see docs/design/system-view-grouping.md.
 */
function renderCrossingMarks(
  marks: CrossingMarks,
  edgeStroke: { color: string; strokeWidth: number }[],
  fallback: { color: string; strokeWidth: number },
): string {
  const r = (n: number): number => Number(n.toFixed(2));
  const strokeOf = (edge: number) => edgeStroke[edge] ?? fallback;
  const parts: string[] = [];
  for (const hop of marks.hops) {
    const rad = (hop.angle * Math.PI) / 180;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const x0 = r(hop.x - hop.halfWidth * c);
    const y0 = r(hop.y - hop.halfWidth * s);
    const x1 = r(hop.x + hop.halfWidth * c);
    const y1 = r(hop.y + hop.halfWidth * s);
    const stroke = strokeOf(hop.edge);
    parts.push(
      el("path", {
        d: `M ${x0} ${y0} A ${r(hop.halfWidth)} ${HOP_RADIUS} ${r(hop.angle)} 0 1 ${x1} ${y1}`,
        fill: "none",
        stroke: stroke.color,
        "stroke-width": stroke.strokeWidth,
      }),
    );
  }
  for (const j of marks.junctions) {
    parts.push(
      el("circle", { cx: r(j.x), cy: r(j.y), r: JUNCTION_RADIUS, fill: strokeOf(j.edge).color }),
    );
  }
  return el("g", { class: "crossing-marks" }, ...parts);
}

/**
 * The ⊕ placeholder a collapsed category folds to (Issue #1821). Drawn at the
 * stub node's laid-out box. Carries `data-collapse-category` so the app's click
 * delegation expands the category (toggling it out of `collapsedCategories`).
 */
function renderCategoryStub(
  node: LayoutNode,
  palette: DiagramPalette,
  /**
   * Facets folded onto this stub (#2174). A collapsed category that hides
   * members of a selected facet still has to say so, or collapsing reads as
   * "nothing here belongs" (TPL-1886).
   */
  facets: readonly string[] = [],
  overlayOn = false,
  facetColorOf?: ReadonlyMap<string, string>,
): string {
  const cat = categoryOf(node) ?? "";
  const cx = node.x + 18;
  const cy = node.y + node.height / 2;
  const rings =
    facets.length > 0 && facetColorOf ? renderFacetRings(node, facets, facetColorOf) : [];
  return el(
    "g",
    {
      class: "krs-category-stub",
      "data-node-id": escapeXml(node.id),
      "data-collapse-category": cat,
      "data-facet-member": facets.length > 0 ? facets.join(" ") : undefined,
      role: "button",
      tabindex: "0",
      opacity: overlayOn && facets.length === 0 ? FACET_DIM_OPACITY : undefined,
    },
    ...rings,
    el("rect", {
      x: node.x,
      y: node.y,
      width: node.width,
      height: node.height,
      rx: 8,
      fill: palette.surfaceBg,
      stroke: palette.mutedBorder,
      "stroke-width": 1,
      "stroke-dasharray": "4 3",
    }),
    ...collapseGlyph(cx, cy, true, palette, { radius: 8, fill: "none" }),
    el(
      "text",
      {
        x: cx + 16,
        y: cy + 4,
        fill: palette.textMuted,
        "font-family": "sans-serif",
        "font-size": 12,
      },
      escapeXml(node.label),
    ),
  );
}

interface Bbox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function bboxOf(nodes: LayoutNode[]): Bbox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    minX = Math.min(minX, n.x);
    minY = Math.min(minY, n.y);
    maxX = Math.max(maxX, n.x + n.width);
    maxY = Math.max(maxY, n.y + n.height);
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Split nodes into contiguous clusters along x, so a tier row (e.g. infra)
 * yields one cluster while side columns (e.g. `[external]` placed left + right)
 * yield one per side — avoiding a frame that spans the whole diagram.
 */
function clusterByXGap(nodes: LayoutNode[], gap: number): LayoutNode[][] {
  const sorted = [...nodes].sort((a, b) => a.x - b.x);
  const clusters: LayoutNode[][] = [];
  let current: LayoutNode[] = [];
  let rightEdge = -Infinity;
  for (const n of sorted) {
    if (current.length > 0 && n.x - rightEdge > gap) {
      clusters.push(current);
      current = [];
    }
    current.push(n);
    rightEdge = Math.max(rightEdge, n.x + n.width);
  }
  if (current.length > 0) clusters.push(current);
  return clusters;
}

/**
 * ⊖ collapse buttons + hover-revealed extent frames for the open `external` /
 * `infra` groups (Issue #1821). The ⊖ is always visible (the collapse control);
 * hovering it reveals the group's dashed frame (`:hover` on the group; the frame
 * is `pointer-events: none` so it never blocks node clicks). Each control carries
 * `data-collapse-category` for the app's click delegation.
 */
function renderCategoryControls(layoutResult: LayoutResult, palette: DiagramPalette): string {
  const byCat = new Map<CategoryId, LayoutNode[]>();
  for (const [, node] of layoutResult.nodes) {
    if (node.tags?.includes(CATEGORY_STUB_TAG)) continue; // already collapsed
    const cat = categoryOf(node);
    if (cat === null) continue;
    const list = byCat.get(cat);
    if (list) list.push(node);
    else byCat.set(cat, [node]);
  }
  if (byCat.size === 0) return "";

  const groups: string[] = [];
  for (const [cat, nodes] of byCat) {
    for (const cluster of clusterByXGap(nodes, 80)) {
      const bb = bboxOf(cluster);
      const pad = 8;
      const fx = bb.minX - pad;
      const fy = bb.minY - pad;
      const fw = bb.maxX - bb.minX + pad * 2;
      const fh = bb.maxY - bb.minY + pad * 2;
      groups.push(
        el(
          "g",
          { class: "krs-cat-group", "data-category-group": cat },
          el("rect", {
            class: "krs-cat-frame",
            x: fx,
            y: fy,
            width: fw,
            height: fh,
            rx: 10,
            fill: "transparent",
            stroke: palette.accent,
            "stroke-width": 1.5,
            "stroke-dasharray": "6 4",
            "pointer-events": "none",
          }),
          el(
            "g",
            {
              class: "krs-cat-collapse",
              "data-collapse-category": cat,
              role: "button",
              tabindex: "0",
              transform: `translate(${fx + fw - 2},${fy + 2})`,
            },
            ...collapseGlyph(0, 0, false, palette),
          ),
        ),
      );
    }
  }
  const style = el(
    "style",
    {},
    ".krs-cat-frame{opacity:0;transition:opacity .1s}" +
      ".krs-cat-group:hover .krs-cat-frame{opacity:1}" +
      ".krs-cat-collapse,.krs-category-stub{cursor:pointer}",
  );
  return el("g", { class: "krs-category-controls" }, style, ...groups);
}

/**
 * A ⊖/⊕ toggle at the top-right of each team boundary frame (system-view Group
 * by, #1858 slice B). Shows ⊖ when the group is expanded (click collapses it to
 * its `<Team> (N)` stub) and ⊕ when collapsed (click expands). Carries
 * `data-collapse-group` for the app's click delegation. Interactive chrome only.
 */
function renderGroupControls(
  layoutResult: LayoutResult,
  palette: DiagramPalette,
  collapsedGroups: ReadonlySet<string> | undefined,
): string {
  const buttons: string[] = [];
  for (const container of layoutResult.containers) {
    if (!container.group || container.groupId === undefined) continue;
    // In-place expansion frames are their own control axis (`data-expand-node`,
    // renderExpandControls) — never a team collapse target (#1921).
    if (container.expanded) continue;
    // Collapsed → ⊕ (click expands); expanded → ⊖ (click collapses).
    const collapsed = collapsedGroups?.has(container.groupId) ?? false;
    const bx = container.x + container.width - 2;
    const by = container.y + 2;
    buttons.push(
      el(
        "g",
        {
          class: "krs-group-collapse",
          "data-collapse-group": container.groupId,
          role: "button",
          tabindex: "0",
          transform: `translate(${bx},${by})`,
        },
        ...collapseGlyph(0, 0, collapsed, palette),
      ),
    );
  }
  if (buttons.length === 0) return "";
  const style = el("style", {}, ".krs-group-collapse{cursor:pointer}");
  return el("g", { class: "krs-group-controls" }, style, ...buttons);
}

/**
 * In-place expansion controls (#1921): a ⊕ on every collapsed service box that
 * has domain children (click expands it in place) and a ⊖ on each expanded
 * container's boundary frame (click collapses it back). Both carry
 * `data-expand-node=<serviceId>` for the app's click delegation. Interactive
 * chrome only — never emitted in static output (mirrors renderGroupControls).
 */
function renderExpandControls(layoutResult: LayoutResult, palette: DiagramPalette): string {
  const buttons: string[] = [];
  // ⊖ on expanded frames (click collapses).
  for (const container of layoutResult.containers) {
    if (!container.expanded || container.nodeId === undefined) continue;
    const bx = container.x + container.width - 2;
    const by = container.y + 2;
    buttons.push(
      el(
        "g",
        {
          class: "krs-expand-control",
          "data-expand-node": container.nodeId,
          role: "button",
          tabindex: "0",
          transform: `translate(${bx},${by})`,
        },
        // expanded → ⊖
        ...collapseGlyph(0, 0, false, palette),
      ),
    );
  }
  // ⊕ on collapsed, drillable service boxes (click expands).
  for (const node of layoutResult.nodes.values()) {
    if (node.kind !== "service" || !node.hasChildren) continue;
    const bx = node.x + node.width - 2;
    const by = node.y + 2;
    buttons.push(
      el(
        "g",
        {
          class: "krs-expand-control",
          "data-expand-node": node.id,
          role: "button",
          tabindex: "0",
          transform: `translate(${bx},${by})`,
        },
        // collapsed → ⊕
        ...collapseGlyph(0, 0, true, palette),
      ),
    );
  }
  if (buttons.length === 0) return "";
  const style = el("style", {}, ".krs-expand-control{cursor:pointer}");
  return el("g", { class: "krs-expand-controls" }, style, ...buttons);
}

/**
 * The outline of a union of axis-aligned rects, as an SVG path `d` (#2179).
 *
 * A frame that reaches an out-of-band member is a rectilinear polygon, not a
 * rect. The union is built by **vertical slabs**: every distinct x becomes a
 * boundary, and each slab's vertical extent is the min/max over the rects
 * covering it. That is exact for the shapes `buildGroupFrames` produces, where
 * every strip shares an edge with the band body so each slab's coverage is one
 * contiguous interval — and it stays readable for any number of strips on either
 * side, which a hand-rolled L/T case analysis does not.
 *
 * Returns `null` for a single rect (nothing to union) and for a slab set that is
 * not contiguous, so the caller falls back to the plain `<rect>` rather than
 * drawing an outline that claims rows it does not cover.
 */
export function rectUnionPath(rects: readonly Rect[]): string | null {
  if (rects.length < 2) return null;
  const xs = [...new Set(rects.flatMap((r) => [r.x, r.x + r.width]))].sort((a, b) => a - b);
  const slabs: { x1: number; x2: number; top: number; bottom: number }[] = [];
  for (let i = 0; i < xs.length - 1; i++) {
    const x1 = xs[i];
    const x2 = xs[i + 1];
    const mid = (x1 + x2) / 2;
    const covering = rects.filter((r) => r.x <= mid && mid <= r.x + r.width);
    if (covering.length === 0) return null; // a gap along x — not one polygon
    const top = Math.min(...covering.map((r) => r.y));
    const bottom = Math.max(...covering.map((r) => r.y + r.height));
    // Contiguity: the covering rects must form one interval, or the outline
    // would fill a hole the frame does not actually cover.
    const sorted = [...covering].sort((a, b) => a.y - b.y);
    let reach = sorted[0].y + sorted[0].height;
    for (const r of sorted.slice(1)) {
      if (r.y > reach) return null;
      reach = Math.max(reach, r.y + r.height);
    }
    const prev = slabs[slabs.length - 1];
    if (prev && prev.x2 === x1 && prev.top === top && prev.bottom === bottom) prev.x2 = x2;
    else slabs.push({ x1, x2, top, bottom });
  }
  const round = (v: number): number => Math.round(v * 100) / 100;
  // Walk the tops left-to-right, then the bottoms right-to-left.
  const points: [number, number][] = [];
  const push = (x: number, y: number): void => {
    const last = points[points.length - 1];
    // Adjacent slabs meet at a shared x, so the naive walk repeats the corner…
    if (last && last[0] === x && last[1] === y) return;
    // …and slabs that differ on only one side leave collinear points along the
    // other. Fold them so the `d` lists corners, which is what a reader diffing
    // a snapshot wants to compare.
    const prev = points[points.length - 2];
    if (
      last &&
      prev &&
      ((prev[0] === last[0] && last[0] === x) || (prev[1] === last[1] && last[1] === y))
    ) {
      points[points.length - 1] = [x, y];
      return;
    }
    points.push([x, y]);
  };
  for (const s of slabs) {
    push(s.x1, s.top);
    push(s.x2, s.top);
  }
  for (const s of [...slabs].reverse()) {
    push(s.x2, s.bottom);
    push(s.x1, s.bottom);
  }
  return `M ${points.map(([x, y]) => `${round(x)} ${round(y)}`).join(" L ")} Z`;
}

/** Frame tint opacity — low enough that two overlapping fills stay distinguishable. */
/** Boundary-frame tint alpha — exported for the contrast guard (#2366 E). */
export const BOUNDARY_TINT_ALPHA = 0.1;
/**
 * `border-style` as the renderer draws it. `dashed` is the frames' default, and
 * `dotted` has to be visibly different from it or the keyword is accepted with
 * no effect.
 */
const FRAME_DASH_ARRAY: Record<"solid" | "dashed" | "dotted", string | undefined> = {
  solid: undefined,
  dashed: "8 4",
  dotted: "2 4",
};

/** Alpha every group-frame tint is drawn at, on both axes. */
const FRAME_FILL_OPACITY = String(BOUNDARY_TINT_ALPHA);

/**
 * The muted strength a group frame's title is drawn at while no style sheet has
 * named a colour for it. The frame is a structural hint at that point, and a
 * full-strength title competes with the cards inside it (ADR-1858).
 */
const MUTED_FRAME_TITLE_OPACITY = 0.7;

/**
 * The cycled default for a boundary, before any `.krs.style` rule is consulted.
 * Callers want {@link resolveBoundaryPaint}, which is the answer surfaces read.
 *
 * `hueIndex` is the boundary's position in the declared order (#2179), so the
 * assignment is stable for a model regardless of band order or collapse state.
 */
function boundaryHueAt(hueIndex: number | undefined, palette?: DiagramPalette): string | undefined {
  if (hueIndex === undefined || palette === undefined) return undefined;
  const hues = palette.boundaryHues;
  return hues.length > 0 ? hues[hueIndex % hues.length] : undefined;
}

/** How one group frame is painted, on every surface that paints it. */
interface FramePaint {
  stroke: string;
  /** The low-alpha frame fill. `undefined` leaves the frame unfilled. */
  fill?: string;
  /** Alpha for {@link fill}; read only when `fill` is set. */
  fillOpacity?: string;
  /** The frame title. */
  title: string;
  /** Title opacity. `undefined` draws it at full strength. */
  titleOpacity?: number;
  strokeWidth: number;
  /**
   * The outline's `stroke-dasharray`, or `undefined` for a solid line. Carries
   * the pattern rather than a `dashed` boolean because `border-style` offers
   * three values: collapsing `dotted` onto the dashed pattern would accept the
   * keyword and render something else (TPL-1503).
   */
  dashArray?: string;
}

/**
 * A boundary's paint, and the only place that answers the question: the author's
 * `.krs.style` rule where there is one, the cycled hue otherwise (#2234 over
 * #2179).
 *
 * A boundary's colour is painted on more than one surface, its frame (stroke,
 * tint and title) and the `◇` 縮退 tab on any card the frame could not reach.
 * Those are drawn by different functions, so the colour is decided once here and
 * read from here by both. Derive it twice and a boundary's identity splits
 * across two colours the moment the derivations stop agreeing, which is exactly
 * what an override does to a second, unaware reader (TPL-2234).
 *
 * `border-color` alone repaints the stroke, the fill and the title together.
 * #2179 established one colour per boundary as a legibility condition rather
 * than a preference, so letting a single declaration split a boundary across
 * two colours would undo the thing the hue exists for. An author who wants them
 * apart says so with `background-color` / `color`.
 */
function resolveBoundaryPaint(
  boundaryId: string | undefined,
  hueIndex: number | undefined,
  frames: ResolvedBoundaryFrames | undefined,
  palette: DiagramPalette | undefined,
): FramePaint | undefined {
  const cycled = boundaryHueAt(hueIndex, palette);
  if (cycled === undefined) return undefined;
  // By the author-facing id, not the group id. A scoped `boundary` (#2036) has
  // identity (declaring scope, id) and carries a scope-qualified group id, while
  // a style sheet only ever writes the bare name. Keying the lookup on the group
  // id would make `boundary#pci` silently inert for every scoped boundary, which
  // reads as a typo rather than as a limitation (TPL-1666 / TPL-1352).
  //
  // So an unqualified selector matches that id in every scope, which is the
  // plain reading of "no qualifier means the qualifier is not being asked
  // about". A way to name one scope can be added later without changing this.
  const key = boundaryId !== undefined ? displayGroupId(boundaryId) : undefined;
  const sheet = (key !== undefined ? frames?.byId.get(key) : undefined) ?? frames?.base;
  const stroke = sheet?.borderColor ?? cycled;
  return {
    stroke,
    fill: sheet?.backgroundColor ?? stroke,
    fillOpacity: FRAME_FILL_OPACITY,
    title: sheet?.color ?? stroke,
    strokeWidth: sheet?.borderWidth ?? 2,
    dashArray: FRAME_DASH_ARRAY[sheet?.borderStyle ?? "dashed"],
  };
}

/**
 * A team frame's paint (#2269), or `undefined` when no author sheet has said
 * anything about this team and the frame keeps the muted dashed look ADR-1858
 * gave it.
 *
 * A team is one entity with two renderings, so the selector that paints its card
 * in the org tree view paints this frame too. Each property lands on the part of
 * the frame that answers to the part of the card it paints there:
 *
 * - `border-color` -> the outline, as it draws the card's border
 * - `background-color` -> the low-alpha tint, as it fills the card
 * - `color` -> the title, as it colours the card's label
 *
 * This is where the team axis parts company with {@link resolveBoundaryPaint},
 * and the asymmetry is deliberate. There, one `border-color` drives all three,
 * because boundary frames overlap and a boundary split across two colours stops
 * reading as an overlap at all (#2179). Team frames never overlap, the axis
 * being 1:1, so nothing forces the three together and following the card is the
 * reading an author can predict from the one declaration they wrote.
 *
 * `frames` holds only what an author declared (`resolveTeamFrames` keeps the
 * built-in sheet out), so an empty answer here really does mean "unstyled"
 * rather than "styled with the card's defaults".
 */
function resolveTeamFramePaint(
  teamId: string | undefined,
  style: ResolvedNodeStyle,
  frames: ResolvedTeamFrames | undefined,
): FramePaint | undefined {
  const named = teamId !== undefined ? frames?.byId.get(teamId) : undefined;
  const sheet = named ?? frames?.base;
  if (sheet === undefined || Object.keys(sheet).length === 0) return undefined;
  return {
    stroke: sheet.borderColor ?? style.borderColor,
    fill: sheet.backgroundColor,
    fillOpacity: sheet.backgroundColor !== undefined ? FRAME_FILL_OPACITY : undefined,
    title: sheet.color ?? style.color,
    // Only a named colour lifts the title out of its muted default: an author
    // who set nothing but `border-width` has not asked for a louder title.
    titleOpacity: sheet.color !== undefined ? undefined : MUTED_FRAME_TITLE_OPACITY,
    strokeWidth: sheet.borderWidth ?? style.borderWidth,
    dashArray: FRAME_DASH_ARRAY[sheet.borderStyle ?? "dashed"],
  };
}

/**
 * The paint for whichever kind of group frame `container` is (#2269).
 *
 * Dispatches on the recorded axis rather than guessing: `groupId` is a boundary
 * id on one axis and an org team id on the other, and an expansion frame sits on
 * neither, so reading the wrong map would let a rule paint a frame it never
 * named.
 */
function resolveFramePaint(
  container: ContainerRect,
  style: ResolvedNodeStyle,
  palette: DiagramPalette | undefined,
  boundaryFrames: ResolvedBoundaryFrames | undefined,
  teamFrames: ResolvedTeamFrames | undefined,
): FramePaint | undefined {
  if (container.groupAxis === "team") {
    return resolveTeamFramePaint(container.groupId, style, teamFrames);
  }
  return resolveBoundaryPaint(container.groupId, container.hueIndex, boundaryFrames, palette);
}

function renderContainer(
  container: ContainerRect,
  style: ResolvedNodeStyle,
  ghost: boolean,
  diffState?: string,
  palette?: DiagramPalette,
  boundaryFrames?: ResolvedBoundaryFrames,
  teamFrames?: ResolvedTeamFrames,
): string {
  const children: string[] = [];
  // An in-place-expanded container is an active user action (#1921): render it
  // prominently — a solid accent border with a faint accent fill — so the opened
  // service reads as a highlighted region rather than the muted, dashed team
  // frame it reuses geometry from. Without this it disappears into a busy diagram.
  const expanded = container.expanded === true;
  const accent = palette?.accent;
  // Boundary frames (#2179) carry an identifying hue and a low-alpha fill of it.
  // The fill is what makes an overlap read as an overlap: two dashed outlines of
  // the same colour read as nesting, while two tints composite to a third tone
  // in the shared cell. No code draws the intersection — alpha does.
  // A `.krs.style` rule may repaint this frame (#2234) or a team frame (#2269);
  // absent one, the cycled hue stands on the boundary axis and the muted dashed
  // default on the team axis.
  const paint = resolveFramePaint(container, style, palette, boundaryFrames, teamFrames);
  const outline = container.coverage ? rectUnionPath(container.coverage) : null;
  // A frame with no style sheet behind it keeps the historical dashed pattern;
  // a ghost container is dashed for the same reason it always was.
  const dashArray = paint ? paint.dashArray : ghost || container.group === true ? "8 4" : undefined;
  const frameShape = {
    fill: expanded && accent ? accent : (paint?.fill ?? "transparent"),
    "fill-opacity": expanded && accent ? "0.06" : paint?.fillOpacity,
    stroke: expanded && accent ? accent : (paint?.stroke ?? style.borderColor),
    "stroke-width": expanded ? 2 : (paint?.strokeWidth ?? style.borderWidth),
    "stroke-dasharray": expanded ? undefined : dashArray,
  };
  children.push(
    outline
      ? // A rectilinear outline cannot take `rx`; rounded joins keep it in the
        // same visual language as the rounded rects it sits among.
        el("path", { d: outline, ...frameShape, "stroke-linejoin": "round" })
      : el("rect", {
          x: container.x,
          y: container.y,
          width: container.width,
          height: container.height,
          ...frameShape,
          rx: style.borderRadius,
        }),
  );
  children.push(
    el(
      "text",
      {
        x: container.x + 12,
        y: container.y + 18,
        // The title takes the hue too. Without it the colour ↔ boundary mapping
        // cannot be recovered from the diagram, and the muted 0.7 the team frames
        // use leaves it close to unreadable (#2179).
        fill: expanded && accent ? accent : (paint?.title ?? style.color),
        "font-size": "12px",
        "font-family": style.fontFamily,
        "font-weight": "bold",
        opacity: expanded ? undefined : paint ? paint.titleOpacity : MUTED_FRAME_TITLE_OPACITY,
      },
      escapeXml(container.label),
    ),
  );

  return el(
    "g",
    {
      "data-container-id": container.id,
      "data-kind-band": container.kindBand,
      "data-group": container.group ? "true" : undefined,
      "data-expanded": expanded ? "true" : undefined,
      "data-diff-state": diffState,
      opacity: ghost ? GHOST_OPACITY : undefined,
    },
    ...children,
  );
}

/**
 * `◇ <boundary>` tabs for memberships no frame could reach (#2179) — the 縮退
 * fallback. Drawn as dashed pills on the card's bottom edge in the frame's own
 * stroke language and hue, so a membership the geometry cannot show still reads
 * as "this card is also inside that frame" rather than as a stray badge.
 *
 * `◇` is U+25C7. The first candidate `⧉` (U+29C9) rasterised as tofu on the PNG
 * path — `packages/app/src/render/png-font-coverage.test.ts` pins the codepoint
 * against the bundled fonts (TPL-1799).
 */
function renderDegradedTabs(
  node: LayoutNode,
  style: ResolvedNodeStyle,
  palette: DiagramPalette,
  boundaryFrames?: ResolvedBoundaryFrames,
): string[] {
  const out: string[] = [];
  // Placement comes from `degraded-tabs.ts`, which port distribution reads too
  // so an edge cannot land under a pill (#2422).
  for (const tab of layoutDegradedTabs(node)) {
    // Through `resolveBoundaryPaint`, not the palette directly: the tab and the
    // frame are two surfaces of one boundary and must never disagree about its
    // colour, including when a `.krs.style` rule repaints it (#2234).
    const hue =
      resolveBoundaryPaint(tab.id, tab.hueIndex, boundaryFrames, palette)?.stroke ??
      palette.mutedBorder;
    out.push(
      el("rect", {
        x: tab.x,
        y: tab.y,
        width: tab.width,
        height: tab.height,
        rx: tab.height / 2,
        fill: palette.canvasBg,
        stroke: hue,
        "stroke-width": 1,
        "stroke-dasharray": "5 3",
      }),
      el(
        "text",
        {
          x: tab.x + tab.width / 2,
          y: tab.y + tab.height / 2,
          "text-anchor": "middle",
          dy: DY_CENTER,
          fill: hue,
          "font-size": "10px",
          "font-family": style.fontFamily,
        },
        escapeXml(tab.label),
      ),
    );
  }
  return out;
}

function renderNode(
  node: LayoutNode,
  style: ResolvedNodeStyle,
  nodeId: string,
  palette: DiagramPalette,
  serviceIdsWithDeploy?: Set<string>,
  displayMode?: DisplayMode,
  childLevelLinks?: Map<string, string>,
  diffState?: string,
  diffMeta?: NodeDiffMeta,
  /**
   * Selected facets this node belongs to, in known-facet order (#2174). Empty
   * when the overlay is off *or* the node is a non-member — the two are told
   * apart by `overlayOn`, because a non-member has to dim while an overlay-off
   * node must render exactly as before.
   */
  facets: readonly string[] = [],
  overlayOn = false,
  facetColorOf?: ReadonlyMap<string, string>,
  /** Boundary frame styles, for the `◇` tabs this card may carry (#2234). */
  boundaryFrames?: ResolvedBoundaryFrames,
  /**
   * True when the layout compensated for shape content insets
   * (LayoutResult.shapeInsetsApplied). Text layout only applies insets
   * then — a padding-only-measured card must keep padding-only drawing.
   */
  applyShapeInsets = false,
  /**
   * True when the viewer wires up the per-node buttons (`RenderOptions.nodeControls`).
   * A static SVG carries no button it cannot honour (#2420).
   */
  nodeControls = false,
): string {
  const children: string[] = [];

  // For icon-mode nodes, render card frame (background + border) before the icon body.
  // Built-in shapes already include fill/stroke in their own rendering.
  const iconFrame = renderIconFrame(node, style, displayMode);
  if (iconFrame) children.push(iconFrame);

  // Shape
  children.push(renderShape(node.x, node.y, node.width, node.height, style));

  // Resolve text positions
  const shapeName = typeof style.shape === "string" ? style.shape : style.shape.url;
  const iconDef = getIconDef(shapeName);

  const textColor = style.color;
  const fontSize = style.fontSize;
  const displayDesc = node.descriptionSummary ?? node.properties.description;
  const hasMetaRow = node.linkCount > 0 || !!node.properties.team;

  if (iconDef?.labelSlot) {
    children.push(
      ...renderSlottedText(node, style, iconDef, displayMode, textColor, fontSize, displayDesc),
    );
  } else {
    children.push(
      ...renderDefaultText(
        node,
        style,
        nodeId,
        textColor,
        fontSize,
        displayDesc,
        hasMetaRow,
        applyShapeInsets,
        displayMode,
      ),
    );
  }

  // Corner lane (#2420): the annotation chip and the icon buttons share one
  // right-packed row, so the chip is measured against the buttons that will be
  // drawn beside it and the two can never collide.
  //
  // Which buttons a node *could* carry is decided by its own data; whether
  // they are actually drawn is decided by `nodeControls` — a static SVG has
  // nothing to click, so the i / D affordances would be noise there
  // (ADR-1821 draws the same line for the category controls).
  const canShowDeployButton =
    DEPLOY_AFFORDANCE_KIND_SET.has(node.kind) && (serviceIdsWithDeploy?.has(nodeId) ?? false);
  // Show info button when the node has any metadata worth displaying in the detail panel.
  // Container nodes (hasChildren) need the button because clicking the body drills down.
  // Leaf nodes also get the button for discoverability, even though clicking the body also opens the panel.
  const canShowInfoButton =
    node.hasDescription || node.linkCount > 0 || !!node.properties.team || !!node.properties.role;
  const showInfoButton = nodeControls && canShowInfoButton;
  const showDeployButton = nodeControls && canShowDeployButton;
  const buttonCount = (showInfoButton ? 1 : 0) + (showDeployButton ? 1 : 0);
  const laneInset = laneInsetOf(style, node, applyShapeInsets);
  const lane = packCornerLane(node, style, buttonCount, palette.badgeFallback, laneInset);

  // Badge (single merged badge driven by the node's current annotations).
  // When diff metadata reports an annotation-only change, the badge is
  // wrapped in `<g data-node-badge data-diff-state="added|removed|unchanged">`
  // so UI can highlight badge churn without painting the whole node amber
  // (Issue #738 / design doc D-2). If the node's badge disappeared because
  // the last annotation was removed, we still emit a ghost badge so the
  // viewer can see *what* was removed.
  const {
    children: badgeParts,
    annotationAddedAttr,
    annotationRemovedAttr,
  } = renderNodeBadge(node, palette, nodeId, diffMeta, lane, buttonCount, laneInset);
  children.push(...badgeParts);

  // Sub-label: shown below the node for ghost domains to indicate the parent service
  const subLabel = renderSubLabel(node, style, textColor, fontSize);
  if (subLabel) children.push(subLabel);

  // Top-right icon buttons, in lane order: [i] rightmost, then [D].
  let btnSlot = 0;

  if (showInfoButton) {
    const btn = lane.buttons[btnSlot++];
    children.push(
      renderIconButton(
        "data-info-button",
        nodeId,
        palette.textMuted,
        "i",
        { fontSize: "10px", italic: true },
        btn.cx,
        btn.cy,
      ),
    );
  }

  if (showDeployButton) {
    const btn = lane.buttons[btnSlot];
    children.push(
      renderIconButton(
        "data-deploy-button",
        nodeId,
        palette.accent,
        "D",
        { fontSize: "9px", bold: true },
        btn.cx,
        btn.cy,
      ),
    );
  }

  // 縮退 tabs paint last so they sit on top of the card body (#2179).
  children.push(...renderDegradedTabs(node, style, palette, boundaryFrames));

  // Facet rings sit above the card body but below the interactive chrome the
  // caller layers on later (category / group / expand controls), so a control
  // is never buried under a ring (TPL-2044).
  if (facets.length > 0 && facetColorOf) {
    children.push(...renderFacetRings(node, facets, facetColorOf));
  }

  // A non-member dims; a member keeps the style's own opacity. When the overlay
  // is off neither applies, which is what makes the no-selection output
  // byte-identical to before this feature existed.
  const dimmed = overlayOn && facets.length === 0;
  const resolvedOpacity = dimmed
    ? FACET_DIM_OPACITY
    : style.opacity < 1
      ? style.opacity
      : undefined;

  const nodeEl = el(
    "g",
    {
      "data-node-id": nodeId,
      "data-node-kind": node.kind,
      "data-has-children": node.hasChildren ? "true" : "false",
      "data-has-description": node.hasDescription ? "true" : "false",
      "data-link-count": node.linkCount > 0 ? String(node.linkCount) : undefined,
      "data-diff-state": diffState,
      "data-annotation-added": annotationAddedAttr,
      "data-annotation-removed": annotationRemovedAttr,
      "data-facet-member": facets.length > 0 ? facets.join(" ") : undefined,
      style: node.hasChildren ? "cursor: pointer" : undefined,
      opacity: resolvedOpacity,
    },
    ...children,
  );

  const childLevelId = childLevelLinks?.get(nodeId);
  if (childLevelId) {
    return el("a", { href: `#${childLevelId}` }, nodeEl);
  }
  return nodeEl;
}

/**
 * Concentric rings, one per selected facet the node belongs to.
 *
 * Drawn off the node's bounding box rather than its shape, so a cylinder, a
 * hexagon and an icon card all get the same treatment (TPL-1001) — matching the
 * outline of every built-in shape would mean a per-shape ring path, and the
 * rings would still read inconsistently between them.
 *
 * Rings grow **outward** in known-facet order, so the innermost ring is the same
 * facet on every node. That is what makes two nodes comparable at a glance; if
 * the order followed each node's own membership the same colour would sit at a
 * different radius from card to card.
 */
function renderFacetRings(
  node: LayoutNode,
  facets: readonly string[],
  colorOf: ReadonlyMap<string, string>,
): string[] {
  const RING_WIDTH = 3;
  const RING_GAP = 1;
  return facets.flatMap((facetId, i) => {
    const color = colorOf.get(facetId);
    if (!color) return [];
    // Ring i sits outside the card by its own half-width plus everything drawn
    // inside it, so strokes never overlap and each colour stays readable.
    const offset = RING_WIDTH / 2 + i * (RING_WIDTH + RING_GAP);
    return [
      el("rect", {
        x: node.x - offset,
        y: node.y - offset,
        width: node.width + offset * 2,
        height: node.height + offset * 2,
        rx: 6 + offset,
        fill: "none",
        stroke: color,
        "stroke-width": RING_WIDTH,
        "data-facet-ring": facetId,
      }),
    ];
  });
}

/**
 * Icon-mode card frame — the background + border rect drawn before the icon
 * body. Built-in shapes already paint their own fill/stroke, so this only
 * applies when the resolved shape is an external icon (`style.shape` is an
 * object, not a built-in shape name) and the view is in icon display mode.
 */
function renderIconFrame(
  node: LayoutNode,
  style: ResolvedNodeStyle,
  displayMode: DisplayMode | undefined,
): string | undefined {
  const isIconShape = typeof style.shape !== "string";
  if (displayMode !== "icon" || !isIconShape) return undefined;
  return el("rect", {
    x: node.x,
    y: node.y,
    width: node.width,
    height: node.height,
    rx: style.borderRadius,
    ry: style.borderRadius,
    fill: style.backgroundColor,
    stroke: style.borderColor,
    "stroke-width": style.borderWidth,
    "stroke-dasharray":
      style.borderStyle === "dashed" ? "8 4" : style.borderStyle === "dotted" ? "2 2" : undefined,
  });
}

/**
 * Text rendered into an icon's declared label/description slots (`krs-label`
 * / `krs-description` positions carried on the SVG icon body) — the icon-mode
 * card's text layout, as opposed to the default centered text stack used by
 * shapes/icons with no slots (see `renderDefaultText`). Caller only invokes
 * this when `iconDef.labelSlot` is present.
 */
function renderSlottedText(
  node: LayoutNode,
  style: ResolvedNodeStyle,
  iconDef: SvgIconDef,
  displayMode: DisplayMode | undefined,
  textColor: string,
  fontSize: number,
  displayDesc: string | undefined,
): string[] {
  const labelSlot = iconDef.labelSlot;
  if (!labelSlot) return [];

  const children: string[] = [];
  const vw = iconDef.viewBoxWidth ?? 24;
  const vh = iconDef.viewBoxHeight ?? 24;
  const scaleX = node.width / vw;
  const scaleY = node.height / vh;

  const labelX = node.x + labelSlot.x * scaleX;
  const labelY = node.y + labelSlot.y * scaleY;
  const labelAnchor = labelSlot.textAnchor ?? "middle";

  // Icon-mode label truncation
  const iconMode = displayMode === "icon";
  const truncatedLabel = iconMode
    ? truncateToWidth(node.label, ICON_LABEL_MAX_WIDTH, ICON_LABEL_CHAR_WIDTH, ICON_LABEL_CJK_WIDTH)
    : node.label;
  const labelFontSize = iconMode ? 13 : fontSize;

  children.push(
    el(
      "text",
      {
        x: labelX,
        y: labelY,
        "text-anchor": labelAnchor,
        dy: DY_CENTER,
        fill: textColor,
        "font-size": `${labelFontSize}px`,
        "font-weight": style.fontWeight,
        "font-family": style.fontFamily,
      },
      escapeXml(truncatedLabel),
    ),
  );

  if (displayDesc && iconDef.descriptionSlot) {
    const descX = node.x + iconDef.descriptionSlot.x * scaleX;
    const descY = node.y + iconDef.descriptionSlot.y * scaleY;
    const descAnchor = iconDef.descriptionSlot.textAnchor ?? "middle";
    const descFontSize = iconMode ? 11 : Math.round(fontSize * RENDERED_DESC_FONT_RATIO);

    if (iconMode) {
      // Multi-line description: wrap text into up to 3 lines with tspan elements
      const lines = wrapToWidth(
        displayDesc,
        ICON_DESC_MAX_WIDTH,
        ICON_DESC_CHAR_WIDTH,
        ICON_DESC_MAX_LINES,
        ICON_DESC_CJK_WIDTH,
      );
      const tspans = lines.map((line, i) =>
        el(
          "tspan",
          {
            x: descX,
            dy: i === 0 ? "0" : `${ICON_DESC_LINE_HEIGHT}`,
          },
          escapeXml(line),
        ),
      );
      children.push(
        el(
          "text",
          {
            x: descX,
            y: descY,
            "text-anchor": descAnchor,
            dy: DY_HANGING,
            fill: textColor,
            "font-size": `${descFontSize}px`,
            "font-family": style.fontFamily,
            opacity: 0.7,
          },
          ...tspans,
        ),
      );
    } else {
      children.push(
        el(
          "text",
          {
            x: descX,
            y: descY,
            "text-anchor": descAnchor,
            dy: DY_CENTER,
            fill: textColor,
            "font-size": `${descFontSize}px`,
            "font-family": style.fontFamily,
            opacity: 0.7,
          },
          escapeXml(displayDesc),
        ),
      );
    }
  }

  return children;
}

const ZERO_INSETS: ShapeInsets = { top: 0, right: 0, bottom: 0, left: 0 };

/** Registry name of a resolved shape (a builtin keyword or an icon URL). */
function shapeNameOf(shape: ResolvedNodeStyle["shape"]): string {
  return typeof shape === "string" ? shape : shape.url;
}

/**
 * The per-node style fallback chain, shared by renderFromLayout and the
 * measurement-time shapeForNode hook so the two can never resolve different
 * styles (#2412 review). `layoutId` is the deploy `containerId::unitId`
 * carrier — resolved styles are keyed by the bare unit id (#735 / #1666).
 */
function resolveNodeStyle(
  styles: ResolvedStyles,
  id: string,
  annotations: readonly string[] | undefined,
  layoutId?: string,
): ResolvedNodeStyle {
  return (
    styles.nodes.get(nodeStyleKey(id, annotations)) ??
    styles.nodes.get(id) ??
    (layoutId !== undefined ? styles.nodes.get(layoutId) : undefined) ??
    styles.defaultNodeStyle
  );
}

/**
 * The two insets the corner lane needs (#2420). Content insets are what the
 * shape registry exposes today, and they are the conservative choice: the lane
 * lands inside the shape's drawn body on every registered shape, where the
 * bounding box would hang the ⓘ over a `user` card's medallion strip or a
 * hexagon's cut corner. Slice C's `portFrame` may offer a tighter outline.
 */
function laneInsetOf(
  style: ResolvedNodeStyle,
  node: LayoutNode,
  applyShapeInsets: boolean,
): LaneInset {
  // Gated on `applyShapeInsets` for the same reason the text is: when the
  // layout measured a card with flat padding, the drawing has to use flat
  // padding too, or the lane drops to a content top the text does not observe
  // and lands on the first line.
  const insets = applyShapeInsets ? shapeInsetsOf(style, node.width, node.height) : ZERO_INSETS;
  return { top: insets.top, right: insets.right };
}

/** Content insets for a node's resolved shape, or zeros (box, external icons). */
function shapeInsetsOf(style: ResolvedNodeStyle, width: number, height: number): ShapeInsets {
  return getShapeContentInset(shapeNameOf(style.shape))?.(width, height) ?? ZERO_INSETS;
}

/**
 * Default centered text stack for shapes/icons with no declared label slot:
 * label, then (as present) description, role, resource badge, capability
 * badge, and the link/team meta row (`renderMetaRow`). Each line grows
 * `nextY` for the next one, and `textLines` centers the whole stack
 * vertically inside the node.
 */
function renderDefaultText(
  node: LayoutNode,
  style: ResolvedNodeStyle,
  nodeId: string,
  textColor: string,
  fontSize: number,
  displayDesc: string | undefined,
  hasMetaRow: boolean,
  applyShapeInsets: boolean,
  displayMode: DisplayMode | undefined,
): string[] {
  const children: string[] = [];
  // Icon mode gives every card the same fixed width (ICON_CARD_WIDTH), so the
  // text has to be fitted to the card rather than the other way round — the
  // shape-mode contract, where measureNode grew the card to fit, does not
  // apply here (#2533).
  const iconMode = displayMode === "icon";
  // This stack spans the whole card (no icon body reserving a column, which is
  // what makes `renderSlottedText`'s narrower ICON_LABEL_MAX_WIDTH right
  // there), so the budget is the card minus the same 8px inset
  // ICON_DESC_MAX_WIDTH already implies for a 160px card.
  const iconTextWidth = node.width - ICON_CARD_TEXT_INSET * 2;
  // The width constants are calibrated for the icon card's own type sizes, so
  // scale them when the style asks for a different one — otherwise a 12px
  // label is measured as if it were 13px and gets cut while it still fits.
  const labelScale = fontSize / ICON_LABEL_FONT_SIZE;
  const descScale = Math.round(fontSize * RENDERED_DESC_FONT_RATIO) / ICON_DESC_FONT_SIZE;
  // Shape-aware content box (#2366 proposal F): insets are content-safe
  // boundaries, clearance per side is max(padding, inset), and the stack
  // centres on the clearance box — exactly the box measureNode reserved —
  // so a cylinder's text sits below the top ellipse and a user card's text
  // centres on the card, not the bounding box.
  const insets = applyShapeInsets ? shapeInsetsOf(style, node.width, node.height) : ZERO_INSETS;
  const clearLeft = Math.max(NODE_PADDING_X, insets.left);
  const clearRight = Math.max(NODE_PADDING_X, insets.right);
  const textX = node.x + clearLeft + (node.width - clearLeft - clearRight) / 2;
  // Wrap the description exactly like layout.ts measureNode did when it
  // reserved the node height, so drawn lines always fit the card (#2366 C).
  const availableWidth = node.width - clearLeft - clearRight;
  const descLines = displayDesc
    ? iconMode
      ? // The shape-mode content box is `width - NODE_PADDING_X * 2`, which on a
        // 160px icon card leaves 80px and breaks a description into stubs. Use
        // the icon card's own budget instead. Line count stays at DESC_MAX_LINES
        // (not the slotted path's 3): this stack is centred in a fixed-height
        // card rather than seated in a declared slot, so the vertical rhythm is
        // unchanged and only the horizontal fit is corrected.
        wrapToWidth(
          displayDesc,
          iconTextWidth,
          ICON_DESC_CHAR_WIDTH * descScale,
          DESC_MAX_LINES,
          ICON_DESC_CJK_WIDTH * descScale,
        )
      : wrapToWidth(
          displayDesc,
          availableWidth,
          CHAR_WIDTH * RENDERED_DESC_FONT_RATIO,
          DESC_MAX_LINES,
        )
    : [];
  const textLines = 1 + descLines.length + (node.properties.role ? 1 : 0) + (hasMetaRow ? 1 : 0);
  const clearTop = Math.max(NODE_PADDING_Y, insets.top);
  const clearBottom = Math.max(NODE_PADDING_Y, insets.bottom);
  let textY = node.y + clearTop + (node.height - clearTop - clearBottom) / 2;
  if (textLines > 1) textY -= ((textLines - 1) * (fontSize + 4)) / 2;

  children.push(
    el(
      "text",
      {
        x: textX,
        y: textY,
        "text-anchor": "middle",
        dy: DY_CENTER,
        fill: textColor,
        "font-size": `${fontSize}px`,
        "font-weight": style.fontWeight,
        "font-family": style.fontFamily,
      },
      escapeXml(
        iconMode
          ? truncateToWidth(
              node.label,
              iconTextWidth,
              ICON_LABEL_CHAR_WIDTH * labelScale,
              ICON_LABEL_CJK_WIDTH * labelScale,
            )
          : node.label,
      ),
    ),
  );

  let nextY = textY + fontSize + 4;

  if (descLines.length > 0) {
    const descFontSize = Math.round(fontSize * RENDERED_DESC_FONT_RATIO);
    for (const line of descLines) {
      children.push(
        el(
          "text",
          {
            x: textX,
            y: nextY,
            "text-anchor": "middle",
            dy: DY_CENTER,
            fill: textColor,
            "font-size": `${descFontSize}px`,
            "font-family": style.fontFamily,
            opacity: SECONDARY_TEXT_OPACITY,
          },
          escapeXml(line),
        ),
      );
      nextY += fontSize + 4;
    }
  }

  if (node.properties.role) {
    children.push(
      el(
        "text",
        {
          x: textX,
          y: nextY,
          "text-anchor": "middle",
          dy: DY_CENTER,
          fill: textColor,
          "font-size": `${Math.round(fontSize * 0.75)}px`,
          "font-family": style.fontFamily,
          "font-style": "italic",
          opacity: 0.8,
        },
        escapeXml(node.properties.role),
      ),
    );
    nextY += fontSize + 4;
  }

  // Resource badge (client only). Replaces the per-resource text loop with
  // a single "📦 ×N" badge so the card height does not grow with resource
  // count. The full list is surfaced in NodeDetailPanel (Issue #914).
  if (node.properties.resources && node.properties.resources.length > 0) {
    const resCount = node.properties.resources.length;
    const resFontSize = Math.round(fontSize * META_FONT_RATIO);
    const tooltip = node.properties.resources.map((r) => `${r.storageKind} "${r.name}"`).join(", ");
    children.push(
      renderCountChip(
        "package",
        resCount,
        tooltip,
        { "data-client-resource-count": String(resCount) },
        textX,
        nextY,
        resFontSize,
        textColor,
        style.fontFamily,
      ),
    );
    nextY += fontSize + 4;
  }

  // Capability badge (client only). Mirrors the resource badge: a single
  // "🔐 ×N" so the card height stays bounded regardless of how many
  // capabilities the client declares. Full list (including label /
  // description) is surfaced in NodeDetailPanel.
  if (node.properties.capabilities && node.properties.capabilities.length > 0) {
    const capCount = node.properties.capabilities.length;
    const capFontSize = Math.round(fontSize * META_FONT_RATIO);
    const tooltip = node.properties.capabilities.map((c) => c.name).join(", ");
    children.push(
      renderCountChip(
        "capability",
        capCount,
        tooltip,
        { "data-client-capability-count": String(capCount) },
        textX,
        nextY,
        capFontSize,
        textColor,
        style.fontFamily,
      ),
    );
    nextY += fontSize + 4;
  }

  // Meta row: link count + team
  if (hasMetaRow) {
    children.push(...renderMetaRow(node, style, nodeId, textX, nextY, clearLeft, clearRight));
  }

  return children;
}

/**
 * A glyph + text pair anchored like SVG text: "start" draws the glyph at x,
 * "end" right-aligns glyph + text at x, "middle" centres the pair on x.
 * Replaces the emoji-prefixed meta strings (#2366 proposal D) — the same
 * geometry `metaChipWidth` reserves during measurement.
 */
function metaChip(
  glyph: MetaGlyphName,
  text: string,
  x: number,
  anchor: "start" | "end" | "middle",
  y: number,
  fontSize: number,
  color: string,
  fontFamily: string,
): string[] {
  const glyphSize = fontSize + 4;
  const textWidth = estimateTextWidth(text, CHAR_WIDTH * META_FONT_RATIO);
  const total = glyphSize + META_GLYPH_GAP + textWidth;
  const left = anchor === "start" ? x : anchor === "end" ? x - total : x - total / 2;
  return [
    metaGlyph(glyph, left, y - glyphSize / 2, glyphSize, color),
    el(
      "text",
      {
        x: left + glyphSize + META_GLYPH_GAP,
        y,
        dy: DY_CENTER,
        fill: color,
        "font-size": `${fontSize}px`,
        "font-family": fontFamily,
      },
      escapeXml(text),
    ),
  ];
}

/** Centred "glyph ×N" chip with a tooltip (client resource / capability rows). */
function renderCountChip(
  glyph: MetaGlyphName,
  count: number,
  tooltip: string,
  dataAttrs: Record<string, string>,
  cx: number,
  y: number,
  fontSize: number,
  color: string,
  fontFamily: string,
): string {
  return el(
    "g",
    { ...dataAttrs, opacity: META_TEXT_OPACITY },
    el("title", {}, escapeXml(tooltip)),
    ...metaChip(glyph, `×${count}`, cx, "middle", y, fontSize, color, fontFamily),
  );
}

/**
 * Link-count / team meta row rendered below a default-text-stack node's
 * badges (Issue #914 / team grouping). `hasMetaRow` (checked by the caller)
 * guarantees at least one of link count / team is present; both, only one,
 * or the layout (left/right split when both) branch on that here.
 *
 * #2366 proposals D + E: the emoji (🔗 / 👥) became vector glyphs, and the
 * row inherits the node's own text color at META_TEXT_OPACITY instead of
 * palette.textSubtle (2.3:1 on the service card background).
 */
function renderMetaRow(
  node: LayoutNode,
  style: ResolvedNodeStyle,
  nodeId: string,
  textX: number,
  nextY: number,
  clearLeft: number,
  clearRight: number,
): string[] {
  const children: string[] = [];
  const metaFontSize = Math.round(style.fontSize * META_FONT_RATIO);
  const color = style.color;
  const buttonAttrs = { style: "cursor: pointer", "pointer-events": "all" };
  const team = node.properties.team;

  const linkChip = (x: number, anchor: "start" | "middle"): string =>
    el(
      "g",
      { "data-link-button": nodeId, ...buttonAttrs, opacity: META_TEXT_OPACITY },
      ...metaChip(
        "link",
        String(node.linkCount),
        x,
        anchor,
        nextY,
        metaFontSize,
        color,
        style.fontFamily,
      ),
    );
  const teamChip = (teamId: string, x: number, anchor: "end" | "middle"): string =>
    el(
      "g",
      { "data-team-button": teamId, ...buttonAttrs, opacity: META_TEXT_OPACITY },
      ...metaChip(
        "team",
        teamChipText(node.properties.teamLabel ?? teamId),
        x,
        anchor,
        nextY,
        metaFontSize,
        color,
        style.fontFamily,
      ),
    );

  if (node.linkCount > 0 && team) {
    // Both link count and team: link on the left, team on the right
    children.push(linkChip(node.x + clearLeft, "start"));
    children.push(teamChip(team, node.x + node.width - clearRight, "end"));
  } else if (node.linkCount > 0) {
    children.push(linkChip(textX, "middle"));
  } else if (team) {
    children.push(teamChip(team, textX, "middle"));
  }

  return children;
}

/**
 * Records each card's corner lane on its layout node, for consumers that must
 * keep clear of it — the edge-port keep-outs of #2422 above all.
 *
 * The reservation counts the buttons the node's own data allows, **not** the
 * ones this render draws: a zone that shrank in static output would route the
 * same diagram's edges differently in the app and on the CLI. Since the chip
 * packs left of the buttons, the interactive packing is always the widest, so
 * reserving it covers both.
 */
function assignChipZones(
  layoutResult: LayoutResult,
  styles: ResolvedStyles,
  palette: DiagramPalette,
  serviceIdsWithDeploy?: Set<string>,
): void {
  const applyShapeInsets = layoutResult.shapeInsetsApplied ?? false;
  for (const [nodeId, node] of layoutResult.nodes) {
    // Stamped here rather than in layout even though layout computes the same
    // rectangle for its port keep-outs (#2422): layout's copy predates
    // `normalizeCoordinates`, which shifts the whole canvas. Recomputing from
    // the final geometry is cheaper than tracking that shift.
    node.chipZone = chipZoneOf(
      node,
      nodeId,
      styles,
      palette,
      applyShapeInsets,
      serviceIdsWithDeploy,
    );
  }
}

/**
 * The corner lane a node's chrome occupies, or undefined for a card that has
 * none. Shared with layout through `LayoutOptions.chipZoneFor` so the keep-out
 * the ports respect is the rectangle the renderer actually fills.
 */
function chipZoneOf(
  node: LayoutNode,
  nodeId: string,
  styles: ResolvedStyles,
  palette: DiagramPalette,
  /** Mirrors what `renderNode` will do: a padding-only layout draws a padding-only lane. */
  applyShapeInsets: boolean,
  serviceIdsWithDeploy?: Set<string>,
): Rect | undefined {
  {
    if (node.tags?.includes(CATEGORY_STUB_TAG)) return undefined;
    const style = resolveNodeStyle(styles, nodeId, node.annotations, node.id);
    const buttonCount =
      (node.hasDescription || node.linkCount > 0 || !!node.properties.team || !!node.properties.role
        ? 1
        : 0) +
      (DEPLOY_AFFORDANCE_KIND_SET.has(node.kind) && (serviceIdsWithDeploy?.has(nodeId) ?? false)
        ? 1
        : 0);
    return packCornerLane(
      node,
      style,
      buttonCount,
      palette.badgeFallback,
      laneInsetOf(style, node, applyShapeInsets),
    ).zone;
  }
}

/**
 * Annotation-driven badge (a single merged badge per node) plus the delta
 * `data-annotation-added` / `data-annotation-removed` attrs the caller
 * stamps onto the node's `<g>` (Issue #738 / design doc D-2). When the
 * node's last annotation was removed there is no current style badge, but a
 * ghost "removed" placeholder is still emitted so the viewer can see what
 * disappeared.
 */
function renderNodeBadge(
  node: LayoutNode,
  palette: DiagramPalette,
  nodeId: string,
  diffMeta: NodeDiffMeta | undefined,
  lane: CornerLane,
  buttonCount: number,
  ghostInset: LaneInset,
): {
  children: string[];
  annotationAddedAttr: string | undefined;
  annotationRemovedAttr: string | undefined;
} {
  const children: string[] = [];
  const annotationsAdded = diffMeta?.changes?.annotations?.added ?? [];
  const annotationsRemoved = diffMeta?.changes?.annotations?.removed ?? [];
  const hasAnnotationDiff = annotationsAdded.length > 0 || annotationsRemoved.length > 0;

  if (lane.chip) {
    // Classify badge diff state. With a single merged badge, direction is:
    //   added.length > 0 → "added" (new annotation produced the current badge)
    //   removed.length > 0 (and none added) → "changed" (swap/rewrite)
    //   no diff or diff doesn't touch annotations → undefined (no attr)
    let badgeDiffState: string | undefined;
    if (annotationsAdded.length > 0) badgeDiffState = "added";
    else if (annotationsRemoved.length > 0) badgeDiffState = "changed";
    children.push(
      el(
        "g",
        { "data-node-badge": nodeId, "data-diff-state": badgeDiffState },
        ...renderChip(lane.chip),
      ),
    );
  } else if (annotationsRemoved.length > 0) {
    // Ghost "removed" badge — all annotations were removed, so there is no
    // current style badge. Render a neutral placeholder with a strike so the
    // user still sees *something was removed*. It rides the same lane, so it
    // cannot land under the buttons either.
    children.push(
      el(
        "g",
        { "data-node-badge": nodeId, "data-diff-state": "removed" },
        ...renderGhostChip(
          laneBox(node, buttonCount, GHOST_CHIP_WIDTH, CHIP_HEIGHT, ghostInset),
          palette.textSubtle,
        ),
      ),
    );
  }
  // Record annotation delta as data-attrs on the node group below so CSS / UI
  // can query the full before/after sets (badge diff is only a hint).
  const annotationAddedAttr =
    hasAnnotationDiff && annotationsAdded.length > 0 ? annotationsAdded.join(",") : undefined;
  const annotationRemovedAttr =
    hasAnnotationDiff && annotationsRemoved.length > 0 ? annotationsRemoved.join(",") : undefined;

  return { children, annotationAddedAttr, annotationRemovedAttr };
}

/**
 * Sub-label rendered below the node body — used for ghost domains to name
 * the parent service they belong to (`(parentLabel)`).
 */
function renderSubLabel(
  node: LayoutNode,
  style: ResolvedNodeStyle,
  textColor: string,
  fontSize: number,
): string | undefined {
  if (!node.subLabel) return undefined;
  const subLabelFontSize = Math.round(fontSize * 0.75);
  return el(
    "text",
    {
      x: node.x + node.width / 2,
      y: node.y + node.height + subLabelFontSize + 4,
      "text-anchor": "middle",
      dy: DY_CENTER,
      fill: textColor,
      "font-size": `${subLabelFontSize}px`,
      "font-family": style.fontFamily,
    },
    escapeXml(`(${node.subLabel})`),
  );
}

/**
 * A top-right circular icon button — the shared `g > circle + text` affordance
 * behind the "i" info button and the "D" deploy button. The two differ only in
 * their `data-*` attribute, color, glyph, font size, and glyph emphasis
 * (`italic` vs `bold`), all threaded through params. The `<text>` attribute
 * order keeps `font-style` before `font-weight`; whichever `opts` flag is unset
 * is emitted as `undefined` and dropped by `el`, so each button's serialized
 * string stays byte-identical to the pre-merge dedicated helpers.
 *
 * - Info button (Issue #914): drawn on nodes with metadata worth surfacing in
 *   the detail panel (description, links, team, role). Container nodes get it
 *   too even though clicking the body also drills down, for discoverability.
 * - Deploy button: drawn on service/domain nodes that have at least one deploy
 *   unit realizing them, to jump to the deploy view for this node.
 */
function renderIconButton(
  dataAttr: string,
  nodeId: string,
  color: string,
  glyph: string,
  opts: { fontSize: string; italic?: boolean; bold?: boolean },
  btnX: number,
  btnY: number,
): string {
  return el(
    "g",
    {
      [dataAttr]: nodeId,
      // Interactive-only chrome: `stripInteractiveChrome` removes this class
      // when the live SVG is exported as a file, so a downloaded diagram
      // matches one rendered statically (#2420).
      class: "krs-node-controls",
      style: "cursor: pointer",
      "pointer-events": "all",
    },
    el("circle", {
      cx: btnX,
      cy: btnY,
      r: 8,
      fill: "transparent",
      stroke: color,
      "stroke-width": 1,
    }),
    el(
      "text",
      {
        x: btnX,
        y: btnY,
        "text-anchor": "middle",
        dy: DY_CENTER,
        fill: color,
        "font-size": opts.fontSize,
        "font-family": "sans-serif",
        "font-style": opts.italic ? "italic" : undefined,
        "font-weight": opts.bold ? "bold" : undefined,
      },
      glyph,
    ),
  );
}

// ---------------------------------------------------------------------------
// Icon-mode text helpers
// ---------------------------------------------------------------------------
